"use client";

import Link from "next/link";
import { Fragment, useMemo, useState } from "react";

import { tournamentSeasonKey, tournamentMatchesFilters, tournamentYears, tournamentType } from "@/lib/tournament-filters";
import TournamentFilters from "./TournamentFilters";
import styles from "./TournamentGrid.module.css";

export type TournamentCardView = {
  id: number;
  slug: string;
  name: string;
  season: string;
  ageCategory: string;
  status: string;
  format: string;
  description: string;
  banner: string;
  teamCount: number;
  matchCount: number;
  totalMatches: number;
  goals: number;
  leader: string | null;
  /** Победитель и призёры турнира, сгруппированные по месту. */
  podium: { name: string; position: number }[];
  finalists?: string[];
  createdAtTs: number;
};

const MEDAL = ["🥇", "🥈", "🥉"];


type Props = {
  items: TournamentCardView[];
  anchor: string;
  title: string;
  subtitle: string;
};

export default function TournamentGrid({ items, anchor, title, subtitle }: Props) {
  const [ageCategory, setAgeCategory] = useState("all");
  const [year, setYear] = useState("all");
  const [type, setType] = useState("all");
  const ages = [...new Set(items.map(t => t.ageCategory))].sort();
  const years = [...new Set(items.flatMap(t => tournamentYears(t.season)))].sort((a,b) => Number(b)-Number(a));
  const types = [...new Set(items.map(t => tournamentType(t.format)))].sort();
  const visible = useMemo(() => items
    .filter((item) => tournamentMatchesFilters(item, {ageCategory,year,season:"all",type}))
    .sort((a, b) => tournamentSeasonKey(b.season) - tournamentSeasonKey(a.season) || b.id - a.id),
    [items, ageCategory, year, type]);

  return (
    <section id={anchor} className="scroll-mt-24 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black uppercase tracking-wide text-white sm:text-3xl">{title}</h2>
          <p className="text-sm text-emerald-200/60">{subtitle}</p>
        </div>
      </div>
      <TournamentFilters fields={[
        {label:"Возраст",value:ageCategory,options:ages,onChange:setAgeCategory},
        {label:"Год",value:year,options:years,onChange:setYear},
        {label:"Тип турнира",value:type,options:types,onChange:setType},
      ]} onReset={()=>{setAgeCategory("all");setYear("all");setType("all");}} />
      <p className="text-sm text-emerald-200/60" aria-live="polite">Найдено турниров: {visible.length} из {items.length}</p>
      {visible.length ? <div className="space-y-3">
        {visible.map((tournament, index) => {
          const current = tournament.status === "current";
          const finalists = !tournament.podium.length ? tournament.finalists ?? [] : [];
          return (
            <Link key={tournament.id} href={`/tournaments/${tournament.slug}`}
              style={{ animationDelay: `${index * 40}ms` }}
              className={`${styles.card} group animate-rise relative block rounded-2xl`}>
              <div className={styles.visual}>
              {/* Real image element avoids CSS-module Base64 bundling and reliably fills the left panel. */}
              <img
                src={tournament.banner?.trim() || (/зима/i.test(tournament.season) ? "/images/tournament-bg-winter.jpg" : /лето/i.test(tournament.season) ? "/images/tournament-bg-pitch-night.jpg" : "/images/tournament-bg-stands.jpg")}
                alt=""
                loading={index < 3 ? "eager" : "lazy"}
                decoding="async"
                onError={(event) => {
                  const fallback = /зима/i.test(tournament.season) ? "/images/tournament-bg-winter.jpg" : /лето/i.test(tournament.season) ? "/images/tournament-bg-pitch-night.jpg" : "/images/tournament-bg-stands.jpg";
                  if (!event.currentTarget.src.endsWith(fallback)) event.currentTarget.src = fallback;
                }}
                className={`${styles.photo} pointer-events-none absolute inset-y-0 left-0 z-0 h-full object-cover object-center`}
                aria-hidden="true"
              />
              <div className={`${styles.cardTint} pointer-events-none absolute inset-0 z-[1]`} />
              </div>
              <div className={styles.layout}>
                <div className={styles.heading}>
                  <span className={styles.metaBadge}>{tournament.season}</span>
                  <h3 className={styles.tournamentTitle}>
                    {tournament.name.replace(/\s+Сезон\s+.*$/i, "").trim()}
                  </h3>
                  <span className={styles.metaBadge}>{tournament.ageCategory}</span>
                  <span className={styles.metaBadge}>{current ? "Текущий" : "Завершён"}</span>
                </div>
                <div className={styles.details}>
                  <p className={styles.detailTitle}>{finalists.length ? "Финалисты" : current ? "Лидеры" : "Призёры"}</p>
                  <p className={styles.detailTitle}>Статистика</p>
                  {[
                    { icon: "👥", value: tournament.teamCount, label: "Команд" },
                    { icon: "⚽", value: tournament.matchCount, label: "Матчей" },
                    { icon: "🥅", value: tournament.goals, label: "Голов" },
                  ].map((stat, row) => {
                    const team = tournament.podium.find((entry) => entry.position === row + 1);
                    return <Fragment key={stat.label}>
                      <span className={styles.podiumTeam}>
                        {team ? <><span aria-hidden="true">{MEDAL[row]}</span><span>{team.name}</span></>
                          : finalists[row] ? <><span aria-hidden="true">⚽</span><span>{finalists[row]}</span></>
                          : <span>{row === 0 && !finalists.length ? (tournament.format === "Кубок" && !tournament.podium.length ? "Итог не установлен" : tournament.leader ?? "—") : ""}</span>}
                      </span>
                      <span className={styles.statRow}>
                        <span aria-hidden="true">{stat.icon}</span>
                        <span className={styles.statValue}>{stat.value}</span>
                        <span>{stat.label}</span>
                      </span>
                    </Fragment>;
                  })}
                </div>
              </div>
            </Link>
          );
        })}
      </div> : <div className="glass-card rounded-2xl p-8 text-center text-emerald-100/60">По выбранному фильтру турниров нет.</div>}
    </section>
  );
}
